/**
 * @author       grady@zumper.com (Grady Kuhnline)
 * @copyright    Copyright (c) 2018, Zumper
 * @description  Memoizes a selector by state and ownProps
 */

import { Tail } from '@zcode/utility-types'
import { shallowEqual } from 'react-redux'

import { Selector } from './types'

const UNDEFINED_STATE =
  '@@zumper/memoizers/memoizeSelector/UNDEFINED_STATE' as const

type ObjectSelectorArgs = [object | undefined, ...any[]]
type NonNullableObjectSelectorArgs = [object, ...any[]]

interface Options<
  Args extends ObjectSelectorArgs | NonNullableObjectSelectorArgs,
> {
  otherArgsEqual?: (args?: Tail<Args>, prevArgs?: Tail<Args>) => boolean
  stateSelector?: Selector<Args, object>
}

interface StateCache<
  Args extends ObjectSelectorArgs | NonNullableObjectSelectorArgs,
  Result,
> {
  otherArgs: Tail<Args> | undefined
  value: Result
}

const shallowEqualEvery = <Args extends Tail<ObjectSelectorArgs>>(
  args?: Args,
  prevArgs?: Args
) => {
  if (!args && !prevArgs) {
    return true
  }
  if (!args || !prevArgs || args.length !== prevArgs.length) {
    return false
  }
  if (args.length === 1) {
    return shallowEqual(args[0], prevArgs[0])
  }
  return !args.some((arg, i) => {
    return !shallowEqual(arg, prevArgs[i])
  })
}

export const undefinedState = Object.freeze({ UNDEFINED_STATE })
const identityStateSelector = (
  ...args: ObjectSelectorArgs | NonNullableObjectSelectorArgs
) => (args?.[0] !== undefined ? args[0] : undefinedState)

export const memoizeSelector = <
  Args extends ObjectSelectorArgs | NonNullableObjectSelectorArgs,
  Result,
>(
  selector: Selector<Args, Result>,
  {
    stateSelector = identityStateSelector,
    otherArgsEqual = shallowEqualEvery,
  }: Options<Args> = {}
): Selector<Args, Result> => {
  const cache = new WeakMap<object, StateCache<Args, Result>>()

  return (...args: Args) => {
    const state = stateSelector(...args)
    const otherArgs =
      Array.isArray(args) && args.length > 1
        ? (args.slice(1) as Tail<Args>)
        : undefined
    let stateCache = cache.get(state)
    if (!stateCache) {
      const value = selector(...args)
      stateCache = {
        otherArgs,
        value,
      }
      if (!state) {
        return value
      }
      cache.set(state, stateCache)
    } else {
      const isEqual = otherArgsEqual(otherArgs, stateCache.otherArgs)
      if (!isEqual) {
        const value = selector(...args)
        stateCache.otherArgs = otherArgs
        stateCache.value = value
      }
    }
    return stateCache.value
  }
}
